import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, filter, firstValueFrom, map, timeout } from 'rxjs';
import { skipAuth } from 'src/app/core/interceptors/auth-context';
import { inAngularZone } from 'src/app/core/offline/in-angular-zone';
import { OfflineAvailabilityService } from 'src/app/core/offline/offline-availability.service';
import { OfflineModeService } from 'src/app/core/offline/offline-mode.service';
import { OnlineWriter } from 'src/app/core/offline/online-writer.service';
import { EnqueueInput, OfflineStore } from 'src/app/core/offline/offline-store.service';
import { OfflineDocumentData, OfflineQuotaError, OutboxEntry, SyncedEvent, isLocalId, newLocalId } from 'src/app/core/offline/offline.models';
import { PdfViewerWarmup } from 'src/app/core/offline/pdf-viewer-warmup.service';
import { REQUEST_TIMEOUT_MS, SyncEngine } from 'src/app/core/offline/sync-engine.service';
import { pdfViewerNeedsLegacyBuild } from 'src/app/shared/models/pdf-viewer-support';
import { environment } from 'src/environments/environment';
import { ListedDocument } from 'src/app/shared/models/document-version.model';
import { DocumentSnapshotData, DocumentUploadFields, DocumentUploadPayload, OfflineDocumentList, SaveOfflineResult, UploadResult } from './document-offline.models';
import { localUploadDocuments, snapshotDocumentList } from './document-overlay';

/** one plan on the device at most (uploads are ≤ 10 MB; the backend flattens PDFs, so a file may grow a little) */
export const MAX_OFFLINE_DOCUMENT_BYTES = 50 * 1024 * 1024;
/** stays free for reports, tasks and photos taken offline (spec §6 «Speicher voll») */
export const OFFLINE_RESERVE_BYTES = 20 * 1024 * 1024;
/** after saving, less than this share of the quota free: warning with the link «Offline-Speicher» */
export const LOW_SPACE_RATIO = 0.1;
/** no progress for this long: the download counts as failed */
export const DOCUMENT_DOWNLOAD_IDLE_MS = REQUEST_TIMEOUT_MS;
/** upload like online: types of the file input and the backend (MulterMiddleware), size below 10 MiB (new-datenraum) */
export const DOCUMENT_UPLOAD_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const DOCUMENT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/** online upload: like a photo upload (120 s), then the same key and body go into the outbox */
export const DOCUMENT_UPLOAD_TIMEOUT_MS = REQUEST_TIMEOUT_MS;

/**
 * E-5 (NA-57): a download of a signed link failed in a way an expired link does (403 from the bucket, or status 0 when the
 * bucket sends its error without CORS headers while the device is online): worth one fresh link. With the Angular service
 * worker the same failed cross-origin request reaches the page as 504 (dev acceptance F1), so 504 while online counts too.
 * The callers renew at most once per open (D48).
 */
export function mayBeExpiredLink(error: unknown, online: boolean): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  return error.status === 403 || ((error.status === 0 || error.status === 504) && online);
}

/** the same rule as the online form: whitelisted type and below 10 MiB */
export function isUploadableFile(file: File): boolean {
  return DOCUMENT_UPLOAD_TYPES.includes(file.type) && file.size < DOCUMENT_UPLOAD_MAX_BYTES;
}

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** the type of a stored file: the key (= the uploaded extension) wins over a generic answer of the bucket */
export function mimeFromKey(key: string, fallback = ''): string {
  const extension = /\.([a-z0-9]+)$/i.exec(key)?.[1]?.toLowerCase() ?? '';
  return MIME_BY_EXTENSION[extension] ?? (fallback || 'application/octet-stream');
}

export interface SaveTarget {
  id: string;
  key: string;
  name: string;
  version?: number | null;
}

type Download = { kind: 'file'; blob: Blob } | { kind: 'too-big'; size: number };

/**
 * Datenraum offline (spec §2, §3, §7.4): documents of a project «Offline verfügbar» are listed from its snapshot; files
 * are on the device only after «Für offline speichern» (never automatically). The removal on refresh (deleted, replaced,
 * no access) lives in OfflineAvailabilityService. Pages keep no own storage (CLAUDE.md «Offline»).
 */
@Injectable({ providedIn: 'root' })
export class DocumentOfflineService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(OfflineStore);
  private readonly availability = inject(OfflineAvailabilityService);
  private readonly mode = inject(OfflineModeService);
  private readonly engine = inject(SyncEngine);
  private readonly warmup = inject(PdfViewerWarmup);
  private readonly online = inject(OnlineWriter);
  private readonly zone = inject(NgZone);

  /** the server answers again: a page that shows device data loads from the server */
  readonly reachable$: Observable<void> = inAngularZone(this.zone, this.mode.online$);
  /** the outbox changed: the Datenraum applies its local uploads and pins again */
  readonly changes$: Observable<void> = inAngularZone(this.zone, this.store.changes$);
  /** a document upload or pin reached the server (no reload, spec §4.1) */
  readonly documentSynced$: Observable<SyncedEvent> = inAngularZone(this.zone, this.engine.synced$).pipe(
    filter((event) => event.type === 'document.upload' || event.type === 'document.pin'),
  );

  isOffline(): boolean {
    return this.mode.isOffline();
  }

  /**
   * a read failed while connected (Funkloch): ask the server, so reachable$ fires once it answers again; true when it
   * answers (the reachability may have been «online» already, then reachable$ does not fire, browser report F2)
   */
  checkReachable(): Promise<boolean> {
    return this.engine.checkReachable();
  }

  /** ids of the documents of this project with a file on this device */
  async savedIds(projectId: string): Promise<Set<string>> {
    const records = await this.store.listSnapshots();
    return new Set(records.filter((record) => record.kind === 'document' && record.projectId === projectId).map((record) => record.refId));
  }

  /** null: the project is not on this device («Offline verfügbar» is off and it was not opened last); local uploads first */
  async listDocuments(projectId: string): Promise<OfflineDocumentList | null> {
    const record = await this.store.getSnapshot<DocumentSnapshotData>(projectId);
    if (!record) return null;
    const list = snapshotDocumentList(record.data, await this.savedIds(projectId));
    const uploads = await this.localUploads(projectId);
    return { ...list, documents: [...uploads, ...list.documents], totalCount: list.totalCount + uploads.length };
  }

  /** documents uploaded on this device and waiting in the outbox (shown online too) */
  async localUploads(projectId: string): Promise<ListedDocument[]> {
    return localUploadDocuments(await this.store.listOutbox(), projectId);
  }

  /**
   * of the local upload cards shown: those whose upload was discarded in the banner (NA-32). One that synced has no outbox
   * entry either, but the id map knows its server id (plan E7): its card stays until the list reload replaces it.
   */
  async discardedUploads(projectId: string, shownIds: string[]): Promise<Set<string>> {
    const waiting = new Set((await this.localUploads(projectId)).map((doc) => doc.id));
    const synced = await this.store.idMap();
    return new Set(shownIds.filter((id) => !waiting.has(id) && !synced.has(id)));
  }

  /** the saved file, or the file of a local upload (documentId = its local id), or null */
  async file(projectId: string, documentId: string): Promise<Blob | null> {
    if (isLocalId(documentId)) {
      const upload = (await this.store.listOutbox()).find((entry) => entry.type === 'document.upload' && entry.entityRef.localId === documentId);
      const blobId = (upload?.payload as unknown as DocumentUploadPayload | undefined)?.blobIds?.[0];
      return blobId ? ((await this.store.getBlob(blobId))?.data ?? null) : null;
    }
    return (await this.store.getSnapshot<OfflineDocumentData>(projectId, 'document', documentId))?.data.file ?? null;
  }

  /**
   * Upload offline (spec §2, §5.6): offline into the outbox (file as Blob, local id); online with Idempotency-Key through
   * the same handler build(): a network error, 5xx or 429 puts the same key and body into the outbox (plan D7 of step 3).
   * Other errors are thrown (the form shows them as today). Type and size as online (plan E9).
   */
  async uploadDocument(projectId: string, fields: DocumentUploadFields, file: File, context: { sharedLinkId?: string | null } = {}): Promise<UploadResult> {
    // shared-link guests upload online only through the plain route (D13); without a signed-in user there is no outbox (NA-34)
    if (context.sharedLinkId) throw new Error('Shared-link guests upload online only (D13)');
    if (!this.store.currentUserId()) throw new Error('Upload without a signed-in user');
    if (!isUploadableFile(file)) return { kind: 'invalid-file' };
    // E8: a local upload is never a predecessor (the server does not know it yet)
    if (fields.predecessorKey && isLocalId(fields.predecessorKey)) {
      throw new Error('A document that only exists on this device cannot be a predecessor');
    }
    const localId = newLocalId();
    const payload: DocumentUploadPayload = { projectId, fields, fileName: file.name, mime: file.type };
    const input: EnqueueInput = {
      type: 'document.upload',
      entityRef: { projectId, localId, serverId: null },
      payload: payload as unknown as Record<string, unknown>,
      blobs: [file],
      userId: this.store.currentUserId() ?? undefined,
    };
    if (!this.isOffline()) {
      const outcome = await this.online.send(input, DOCUMENT_UPLOAD_TIMEOUT_MS);
      if (outcome.kind === 'done') {
        void this.availability.refresh(projectId, { fresh: true });
        const documents = (outcome.body as { data?: unknown[] } | null)?.data;
        return { kind: 'uploaded', documents: Array.isArray(documents) ? documents : [] };
      }
      if (outcome.key) input.idempotencyKey = outcome.key;
    }
    try {
      await this.store.enqueue(input);
    } catch (error) {
      if (error instanceof OfflineQuotaError) return { kind: 'quota' };
      throw error;
    }
    void this.engine.run();
    return { kind: 'queued', localId };
  }

  /**
   * «Für offline speichern» (spec §3), only online. A fresh signed link, then the file with progress: bigger than
   * MAX_OFFLINE_DOCUMENT_BYTES or than the free space minus OFFLINE_RESERVE_BYTES aborts the download (plan E2).
   */
  async saveForOffline(projectId: string, target: SaveTarget): Promise<SaveOfflineResult> {
    if (this.isOffline()) return { kind: 'offline' };
    const userId = this.store.currentUserId();
    if (!userId) return { kind: 'failed' };
    const estimate = await this.store.estimate();
    const free = estimate && estimate.quota > 0 ? Math.max(0, estimate.quota - estimate.usage) : null;
    const spaceLimit = free === null ? Number.POSITIVE_INFINITY : free - OFFLINE_RESERVE_BYTES;
    let download: Download;
    try {
      const url = await this.signedUrl(projectId, target.key);
      download = await this.download(url, Math.min(MAX_OFFLINE_DOCUMENT_BYTES, spaceLimit));
    } catch {
      return { kind: 'failed' };
    }
    const size = download.kind === 'file' ? download.blob.size : download.size;
    if (size > MAX_OFFLINE_DOCUMENT_BYTES) return { kind: 'too-large', size };
    if (size > spaceLimit) return { kind: 'no-space', size };
    if (download.kind !== 'file') return { kind: 'failed' };

    const mime = mimeFromKey(target.key, download.blob.type);
    const data: OfflineDocumentData = {
      documentId: target.id,
      key: target.key,
      name: target.name,
      version: target.version ?? 1,
      mime,
      size,
      file: download.blob.type === mime ? download.blob : new Blob([download.blob], { type: mime }),
    };
    try {
      if (!(await this.availability.saveDocument(projectId, data, userId))) return { kind: 'failed' };
    } catch (error) {
      if (error instanceof OfflineQuotaError) return { kind: 'quota' };
      return { kind: 'failed' };
    }
    if (mime === 'application/pdf') void this.warmup.warm(pdfViewerNeedsLegacyBuild());
    // the snapshot may predate the document (uploaded a moment ago): a fresh one lists the plan offline right away (NA-01)
    void this.availability.refresh(projectId, { fresh: true });
    const lowSpace = free !== null && estimate !== null && free - size < estimate.quota * LOW_SPACE_RATIO;
    return { kind: 'saved', size, lowSpace };
  }

  /**
   * E14 revised (user decision 2026-10-11): a plan uploaded from the outbox stays «für offline gespeichert» after the sync.
   * The engine calls this from DocumentUploadHandler.onSuccess, before it removes the entry: the file comes from the
   * outbox (no second download). Limits as «Für offline speichern» (plan E2 of step 4); too big or too little space:
   * nothing is kept (the upload itself is done). Online uploads without the outbox are not kept (D40). Never throws.
   */
  async keepUploadedFile(entry: OutboxEntry, body: unknown): Promise<boolean> {
    try {
      const created = (body as { data?: { id?: unknown; key?: unknown; name?: unknown; version?: unknown }[] } | null)?.data?.[0];
      const payload = entry.payload as unknown as DocumentUploadPayload;
      const blobId = payload.blobIds?.[0];
      if (typeof created?.id !== 'string' || typeof created.key !== 'string' || !blobId) return false;
      const blob = (await this.store.getBlob(blobId, entry.userId))?.data;
      if (!blob || blob.size > MAX_OFFLINE_DOCUMENT_BYTES) return false;
      const estimate = await this.store.estimate();
      if (estimate && estimate.quota > 0 && estimate.quota - estimate.usage - blob.size < OFFLINE_RESERVE_BYTES) return false;
      const mime = mimeFromKey(created.key, payload.mime);
      const data: OfflineDocumentData = {
        documentId: created.id,
        key: created.key,
        name: typeof created.name === 'string' && created.name ? created.name : payload.fields.name,
        version: typeof created.version === 'number' ? created.version : 1,
        mime,
        size: blob.size,
        file: blob.type === mime ? blob : new Blob([blob], { type: mime }),
      };
      // inside the sync run (Web Lock held): no persist question, no quota toast, the snapshot is not awaited (Rev1 H3)
      if (!(await this.availability.saveDocument(entry.entityRef.projectId, data, entry.userId, { background: true }))) return false;
      if (mime === 'application/pdf') void this.warmup.warm(pdfViewerNeedsLegacyBuild());
      return true;
    } catch (error) {
      console.warn('[DocumentOfflineService] uploaded plan could not be kept on the device', error);
      return false;
    }
  }

  async removeOffline(projectId: string, documentId: string): Promise<void> {
    await this.availability.removeDocument(projectId, documentId);
  }

  /**
   * E-5 (NA-57): a fresh signed link of one document (60 min). Pages whose list links may have expired (opened long ago)
   * fetch one before they give up. Members: GET …/documents/:key. Shared-link guests have no route for one document:
   * their list (GET …/projects/shared/:id/documents, without login) is read again and the link of the same key taken
   * (E-5 Rev1: links 60 min everywhere). Never stored.
   */
  freshLink(projectId: string | null, key: string, sharedLinkId: string | null = null): Promise<string> {
    if (sharedLinkId) return this.sharedLink(sharedLinkId, key);
    if (!projectId) return Promise.reject(new Error('fresh link without project'));
    return this.signedUrl(projectId, key);
  }

  private async signedUrl(projectId: string, key: string): Promise<string> {
    const url = `${environment.apiUrl}/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(key)}`;
    const response = await firstValueFrom(this.http.get<{ data: { url?: string } }>(url).pipe(timeout(REQUEST_TIMEOUT_MS)));
    if (!response?.data?.url) throw new Error('document without signed link');
    return response.data.url;
  }

  private async sharedLink(sharedLinkId: string, key: string): Promise<string> {
    const url = `${environment.apiUrl}/projects/shared/${encodeURIComponent(sharedLinkId)}/documents`;
    const response = await firstValueFrom(
      this.http.get<{ data?: { key?: unknown; url?: unknown }[] }>(url, { context: skipAuth() }).pipe(timeout(REQUEST_TIMEOUT_MS)),
    );
    const link = (response?.data ?? []).find((doc) => doc?.key === key)?.url;
    if (typeof link !== 'string' || !link) throw new Error('document without signed link');
    return link;
  }

  /** the first progress event with a known total above the limit ends the request (firstValueFrom unsubscribes) */
  private download(url: string, limit: number): Promise<Download> {
    return firstValueFrom(
      this.http.get(url, { responseType: 'blob', observe: 'events', reportProgress: true }).pipe(
        timeout(DOCUMENT_DOWNLOAD_IDLE_MS),
        map((event): Download | null => {
          if (event.type === HttpEventType.DownloadProgress && event.total && event.total > limit) return { kind: 'too-big', size: event.total };
          if (event.type === HttpEventType.Response) return { kind: 'file', blob: event.body as Blob };
          return null;
        }),
        filter((value): value is Download => value !== null),
      ),
    );
  }
}
